import { useCallback, useMemo } from "react";
import { useAuth } from "@sal/authentication.react";
import type { LookupItem } from "@origin/platform-types";
import { SecurityContext, UserGroupName } from "../constants";

const restrictedUserGroupNames = [
	UserGroupName.MOVEAdministrators,
	UserGroupName.MOVE,
	UserGroupName.CustomerAdministrators
] as const;

type RestrictedUserGroup = {
	name: string;
	canManage: boolean;
};

export function useRestrictedUserGroup() {
	const auth = useAuth();
	const canManageAdminGroup = auth.userHasPermission(SecurityContext.AdminGroupUser, "Write");

	const restrictedUserGroups = useMemo<RestrictedUserGroup[]>(
		() => restrictedUserGroupNames.map((name) => ({
			name,
			canManage: canManageAdminGroup || auth.userHasPermission(SecurityContext.ManageUserGroup, name)
		})),
		[canManageAdminGroup, auth]
	);

	const filterUserGroupOptions = useCallback(
		<T extends LookupItem>(options: T[]): T[] => {
			return options.filter((option) => {
				const restrictedUserGroup = restrictedUserGroups.find((p) => p.name === option.name);
				return restrictedUserGroup ? restrictedUserGroup.canManage : true;
			});
		},
		[restrictedUserGroups]
	);

	return { restrictedUserGroups, filterUserGroupOptions };
}
